import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useNavigate, useParams} from 'react-router-dom';
import {
    archivePost,
    assignHeroMedia,
    checkSlugAvailability,
    clearHeroMedia,
    createPost,
    getPost,
    previewPost,
    publishPost,
    registerMedia,
    restorePost,
    schedulePost,
    unpublishPost,
    updatePost
} from '../../api/blog';
import {PostDto, PostVisibility, RichContentDocument} from '../../api/blogTypes';
import {uploadFile} from '../../api/files';
import {
    createContentDocumentFromBlocks,
    createEmptyContentDocument,
    ensureContentDocument,
    parseMarkdownToContent,
    pruneEmptyContentDocument,
    renderContentDocumentToHtml,
    serializeContentDocumentToMarkdown
} from '../../utils/blogContent';
import {pickImageUrl} from '../../utils/imageUrls';
import LexicalContentEditor from './components/LexicalContentEditor';
import {
    ArrowLeft, Eye, Send, CalendarClock, Save, Image as ImageIcon, X,
    Trash2, RotateCcw, FileText
} from 'lucide-react';
import './BlogPostEditorPage.css';

interface ToastMessage {
    id: number;
    type: 'success' | 'error' | 'info';
    message: string;
}

interface FormState {
    title: string;
    slug: string;
    excerpt: string;
    pageTitle: string;
    metaDescription: string;
    visibility: PostVisibility;
    bodyMarkdown: string;
    contentDocument: RichContentDocument;
    readingTime: number;
    noindex: boolean;
    nofollow: boolean;
}

type ExtendedUpdatePostRequest = Parameters<typeof updatePost>[1] & {
    noindex?: boolean;
    nofollow?: boolean;
};

const DEFAULT_DRAFT_BODY_MARKDOWN = 'Draft post content...';

const defaultFormState: FormState = {
    title: '',
    slug: '',
    excerpt: '',
    pageTitle: '',
    metaDescription: '',
    visibility: 'PRIVATE',
    bodyMarkdown: '',
    contentDocument: createEmptyContentDocument(),
    readingTime: 0,
    noindex: false,
    nofollow: false
};

const extractErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return 'Unexpected error occurred. Please try again.';
};

const BlogPostEditorPage: React.FC = () => {
    const {postId} = useParams<{ postId?: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [formState, setFormState] = useState<FormState>(defaultFormState);
    const [scheduleValue, setScheduleValue] = useState('');
    const [previewHtml, setPreviewHtml] = useState<string | null>(null);
    const [slugCheckFeedback, setSlugCheckFeedback] = useState<string | null>(null);
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    const [isInlineImageUploading, setIsInlineImageUploading] = useState(false);
    const [previewMode, setPreviewMode] = useState<'realtime' | 'sanitized'>('realtime');
    const creatingDraftRef = useRef(false);
    const initializedPostIdRef = useRef<number | null>(null);
    const slugAvailabilityTimeoutRef = useRef<number | null>(null);
    const lastCheckedSlugRef = useRef<string>('');
    const previewRefreshTimeoutRef = useRef<number | null>(null);
    const [cardImagePreview, setCardImagePreview] = useState<string | null>(null);
    // Key for remounting the editor when data loads from server
    const [editorKey, setEditorKey] = useState<number>(0);

    const numericPostId = useMemo(() => {
        if (!postId) {
            return null;
        }
        const parsed = Number(postId);
        return Number.isFinite(parsed) ? parsed : null;
    }, [postId]);

    const apiBaseUrl = useMemo(() => {
        const base = import.meta.env.VITE_API_BASE_URL ?? '';
        return base.endsWith('/') ? base.slice(0, -1) : base;
    }, []);

    const composeImageUrl = useCallback(
        (filename: string) => (apiBaseUrl ? `${apiBaseUrl}/images/${filename}` : filename),
        [apiBaseUrl]
    );

    const computedPreviewHtml = useMemo(
        () => renderContentDocumentToHtml(formState.contentDocument, {resolveImageSrc: composeImageUrl}),
        [composeImageUrl, formState.contentDocument]
    );

    const pushToast = (type: ToastMessage['type'], message: string) => {
        setToasts((prev) => [...prev, {id: Date.now() + Math.random(), type, message}]);
    };

    const dismissToast = (id: number) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    };

    const refreshPostQuery = () => {
        if (numericPostId) {
            void queryClient.invalidateQueries({queryKey: ['blog-post', numericPostId]});
        }
    };

    const handleContentChange = useCallback((contentDocument: RichContentDocument, markdown: string) => {
        setFormState(prev => ({...prev, contentDocument, bodyMarkdown: markdown}));
        setPreviewHtml(null);
    }, []);

    const handleRichImageUpload = async (file: File) => {
        setIsInlineImageUploading(true);
        try {
            const response = await uploadFile(file);
            const storedName = Object.values(response)[0];
            if (typeof storedName !== 'string' || !storedName) {
                throw new Error('Upload did not return a filename.');
            }

            await registerMedia({filename: storedName, kind: 'IMAGE'});

            const defaultAlt = file.name.replace(/\.[^.]+$/, '');
            const alt = window.prompt('Image alt text', defaultAlt) ?? defaultAlt;
            const caption = window.prompt('Image caption (optional)') ?? '';

            pushToast('success', 'Image uploaded and ready to use.');
            return {
                src: storedName,
                alt: alt.trim() || undefined,
                caption: caption.trim() || undefined
            };
        } catch (error) {
            pushToast('error', extractErrorMessage(error));
            throw error;
        } finally {
            setIsInlineImageUploading(false);
        }
    };

    const createPostMutation = useMutation({
        mutationFn: async () =>
            createPost({
                title: 'Untitled post',
                slug: `untitled-${Date.now()}`,
                visibility: 'PRIVATE',
                body_markdown: DEFAULT_DRAFT_BODY_MARKDOWN,
                content_document: createContentDocumentFromBlocks(
                    parseMarkdownToContent(DEFAULT_DRAFT_BODY_MARKDOWN)
                ),
                reading_time_minutes: 0
            }),
        onSuccess: (created) => {
            pushToast('success', 'Draft post created.');
            creatingDraftRef.current = false;
            navigate(`/blog/posts/${created.id}`, {replace: true});
        },
        onError: (error: unknown) => {
            pushToast('error', extractErrorMessage(error));
            creatingDraftRef.current = false;
        }
    });

    useEffect(() => {
        if (!numericPostId && !creatingDraftRef.current) {
            creatingDraftRef.current = true;
            createPostMutation.mutate();
        }
    }, [createPostMutation, numericPostId]);

    const postQuery = useQuery<PostDto>({
        queryKey: ['blog-post', numericPostId],
        queryFn: () => getPost(numericPostId!),
        enabled: numericPostId !== null
    });

    useEffect(() => {
        if (postQuery.data && initializedPostIdRef.current !== postQuery.data.id) {
            const post = postQuery.data;
            const isDefaultDraftBody = post.body_markdown === DEFAULT_DRAFT_BODY_MARKDOWN;
            const hydratedDocument = ensureContentDocument(post.content_document, post.body_markdown);
            initializedPostIdRef.current = post.id;
            const normalizedDocument = isDefaultDraftBody
                ? createEmptyContentDocument()
                : hydratedDocument;
            setFormState({
                title: post.title,
                slug: post.slug,
                excerpt: post.excerpt ?? '',
                pageTitle: post.page_title ?? '',
                metaDescription: post.meta_description ?? '',
                visibility: post.visibility,
                bodyMarkdown: serializeContentDocumentToMarkdown(normalizedDocument),
                contentDocument: normalizedDocument,
                readingTime: post.reading_time_minutes ?? 0,
                noindex: post.noindex,
                nofollow: post.nofollow
            });
            setPreviewHtml(
                isDefaultDraftBody
                    ? null
                    : post.body_html ?? renderContentDocumentToHtml(normalizedDocument, {resolveImageSrc: composeImageUrl})
            );
            // Remount the editor to pick up the new content from server
            setEditorKey(prev => prev + 1);
        }
    }, [composeImageUrl, postQuery.data]);

    const updateMutation = useMutation({
        mutationFn: (payload: ExtendedUpdatePostRequest) => updatePost(numericPostId!, payload),
        onSuccess: (updated) => {
            pushToast('success', 'Post saved successfully.');
            // Only update metadata fields - NEVER update contentDocument or bodyMarkdown
            // The editor is the source of truth for content during editing
            setFormState((prev) => ({
                ...prev,
                slug: updated.slug, // Server may normalize the slug
                readingTime: updated.reading_time_minutes ?? prev.readingTime
                // Intentionally NOT updating: contentDocument, bodyMarkdown
            }));
            if (updated.body_html) {
                setPreviewHtml(updated.body_html);
            }
            if (updated.hero) {
                const previewFromHero = pickImageUrl(updated.hero.image_urls ?? null, updated.hero.filename ?? null);
                setCardImagePreview(previewFromHero);
            }
            refreshPostQuery();
        },
        onError: (error: unknown) => {
            pushToast('error', extractErrorMessage(error));
        }
    });

    const publishMutation = useMutation({
        mutationFn: () => publishPost(numericPostId!, null),
        onSuccess: () => {
            pushToast('success', 'Post published.');
            refreshPostQuery();
        },
        onError: (error: unknown) => pushToast('error', extractErrorMessage(error))
    });

    const unpublishMutation = useMutation({
        mutationFn: () => unpublishPost(numericPostId!),
        onSuccess: () => {
            pushToast('success', 'Post unpublished.');
            refreshPostQuery();
        },
        onError: (error: unknown) => pushToast('error', extractErrorMessage(error))
    });

    const archiveMutation = useMutation({
        mutationFn: () => archivePost(numericPostId!),
        onSuccess: () => {
            pushToast('success', 'Post archived.');
            refreshPostQuery();
        },
        onError: (error: unknown) => pushToast('error', extractErrorMessage(error))
    });

    const restoreMutation = useMutation({
        mutationFn: () => restorePost(numericPostId!),
        onSuccess: () => {
            pushToast('success', 'Post restored.');
            refreshPostQuery();
        },
        onError: (error: unknown) => pushToast('error', extractErrorMessage(error))
    });

    const scheduleMutation = useMutation({
        mutationFn: (iso: string) => schedulePost(numericPostId!, {scheduled_at: iso}),
        onSuccess: () => {
            pushToast('success', 'Post scheduled.');
            setScheduleValue('');
            refreshPostQuery();
        },
        onError: (error: unknown) => pushToast('error', extractErrorMessage(error))
    });

    const slugCheckMutation = useMutation({
        mutationFn: (slug: string) => checkSlugAvailability(slug),
        onSuccess: (result, slug) => {
            lastCheckedSlugRef.current = slug;
            setSlugCheckFeedback(result.available ? 'Slug is available.' : 'Slug is already taken.');
        },
        onError: (error: unknown) => {
            pushToast('error', extractErrorMessage(error));
        }
    });

    const previewMutation = useMutation({
        mutationFn: () =>
            previewPost({
                body_markdown: formState.bodyMarkdown,
                content_document: pruneEmptyContentDocument(formState.contentDocument)
            }),
        onSuccess: (result) => {
            setPreviewHtml(result.body_html);
            pushToast('info', 'Sanitized preview updated.');
        },
        onError: (error: unknown) => pushToast('error', extractErrorMessage(error))
    });

    const isBusy =
        updateMutation.isPending ||
        publishMutation.isPending ||
        unpublishMutation.isPending ||
        archiveMutation.isPending ||
        restoreMutation.isPending ||
        scheduleMutation.isPending;

    const handleInputChange = useCallback(
        <K extends keyof FormState>(key: K, value: FormState[K]) => {
            setFormState((prev) => ({...prev, [key]: value}));
        },
        []
    );

    const handleSave = () => {
        if (!numericPostId) {
            return;
        }
        const sanitizedDocument = pruneEmptyContentDocument(formState.contentDocument);

        updateMutation.mutate({
            title: formState.title,
            slug: formState.slug,
            excerpt: formState.excerpt || null,
            page_title: formState.pageTitle || null,
            meta_description: formState.metaDescription || null,
            visibility: formState.visibility,
            body_markdown: serializeContentDocumentToMarkdown(sanitizedDocument),
            content_document: sanitizedDocument,
            reading_time_minutes: Number.isFinite(formState.readingTime) ? formState.readingTime : null,
            noindex: formState.noindex,
            nofollow: formState.nofollow
        });
    };

    const handleSchedule = () => {
        if (!scheduleValue) {
            pushToast('info', 'Pick date and time for schedule.');
            return;
        }
        const date = new Date(scheduleValue);
        if (Number.isNaN(date.getTime())) {
            pushToast('error', 'Invalid schedule date.');
            return;
        }
        scheduleMutation.mutate(date.toISOString());
    };

    useEffect(() => {
        if (!formState.slug.trim()) {
            setSlugCheckFeedback(null);
            lastCheckedSlugRef.current = '';
            if (slugAvailabilityTimeoutRef.current) {
                window.clearTimeout(slugAvailabilityTimeoutRef.current);
            }
            return;
        }
        const slug = formState.slug.trim();
        if (slug === lastCheckedSlugRef.current) {
            return;
        }
        if (slugAvailabilityTimeoutRef.current) {
            window.clearTimeout(slugAvailabilityTimeoutRef.current);
        }
        slugAvailabilityTimeoutRef.current = window.setTimeout(() => {
            setSlugCheckFeedback('Checking availability…');
            slugCheckMutation.mutate(slug);
        }, 500);
        return () => {
            if (slugAvailabilityTimeoutRef.current) {
                window.clearTimeout(slugAvailabilityTimeoutRef.current);
            }
        };
    }, [formState.slug, slugCheckMutation]);

    useEffect(() => {
        if (previewMode !== 'sanitized') {
            if (previewRefreshTimeoutRef.current) {
                window.clearTimeout(previewRefreshTimeoutRef.current);
                previewRefreshTimeoutRef.current = null;
            }
            return;
        }
        if (!formState.bodyMarkdown.trim() && formState.contentDocument.blocks.length === 0) {
            setPreviewHtml(null);
            return;
        }
        if (previewRefreshTimeoutRef.current) {
            window.clearTimeout(previewRefreshTimeoutRef.current);
        }
        previewRefreshTimeoutRef.current = window.setTimeout(() => {
            if (!previewMutation.isPending) {
                previewMutation.mutate();
            }
        }, 450);
        return () => {
            if (previewRefreshTimeoutRef.current) {
                window.clearTimeout(previewRefreshTimeoutRef.current);
            }
        };
    }, [formState.bodyMarkdown, formState.contentDocument, previewMode, previewMutation]);

    useEffect(
        () => () => {
            if (slugAvailabilityTimeoutRef.current) {
                window.clearTimeout(slugAvailabilityTimeoutRef.current);
            }
            if (previewRefreshTimeoutRef.current) {
                window.clearTimeout(previewRefreshTimeoutRef.current);
            }
        },
        []
    );

    // 30-second debounced autosave (spec: blog editor + model edit auto-save drafts every 30s)
    const autosaveTimerRef = useRef<number | null>(null);
    const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
    useEffect(() => {
        if (!numericPostId || !initializedPostIdRef.current) return;
        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = window.setTimeout(() => {
            if (!updateMutation.isPending) {
                const sanitized = pruneEmptyContentDocument(formState.contentDocument);
                updateMutation.mutate({
                    title: formState.title,
                    slug: formState.slug,
                    excerpt: formState.excerpt || null,
                    page_title: formState.pageTitle || null,
                    meta_description: formState.metaDescription || null,
                    visibility: formState.visibility,
                    body_markdown: serializeContentDocumentToMarkdown(sanitized),
                    content_document: sanitized,
                    reading_time_minutes: Number.isFinite(formState.readingTime) ? formState.readingTime : null,
                    noindex: formState.noindex,
                    nofollow: formState.nofollow
                });
                setLastSavedAt(Date.now());
            }
        }, 30000);
        return () => {
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formState, numericPostId]);

    const cardImageUploadMutation = useMutation({
        mutationFn: async (file: File) => {
            if (!numericPostId) {
                throw new Error('Cannot upload an image before the post is created.');
            }
            const response = await uploadFile(file);
            const storedName = Object.values(response)[0];
            if (typeof storedName !== 'string' || !storedName) {
                throw new Error('Upload did not return a filename.');
            }
            await registerMedia({filename: storedName, kind: 'IMAGE'});
            const normalizedTitle = formState.title.trim();
            const fallbackAlt = file.name.replace(/\.[^.]+$/, '');
            const altText = normalizedTitle || fallbackAlt;
            const normalizedExcerpt = formState.excerpt.trim();
            const updated = await assignHeroMedia(numericPostId, {
                filename: storedName,
                alt_text: altText || null,
                caption: normalizedExcerpt || null
            });
            return updated;
        },
        onSuccess: (updatedPost) => {
            const nextPreview = pickImageUrl(updatedPost.hero?.image_urls ?? null, updatedPost.hero?.filename ?? null);
            setCardImagePreview(nextPreview);
            pushToast('success', 'Card image updated.');
            refreshPostQuery();
        },
        onError: (error: unknown) => {
            pushToast('error', extractErrorMessage(error));
        }
    });

    const cardImageRemoveMutation = useMutation({
        mutationFn: async () => {
            if (!numericPostId) {
                throw new Error('Cannot update card image before the post is created.');
            }
            const updated = await clearHeroMedia(numericPostId);
            return updated;
        },
        onSuccess: (updatedPost) => {
            const nextPreview = pickImageUrl(updatedPost.hero?.image_urls ?? null, updatedPost.hero?.filename ?? null);
            setCardImagePreview(nextPreview);
            pushToast('success', 'Card image removed.');
            refreshPostQuery();
        },
        onError: (error: unknown) => {
            pushToast('error', extractErrorMessage(error));
        }
    });

    const heroFilename = postQuery.data?.hero?.filename ?? null;
    const heroImageUrls = postQuery.data?.hero?.image_urls ?? null;
    const heroPreviewImage = useMemo(
        () => pickImageUrl(heroImageUrls ?? null, heroFilename ?? null),
        [heroImageUrls, heroFilename]
    );

    useEffect(() => {
        if (postQuery.isFetching && !postQuery.isError) {
            return;
        }
        setCardImagePreview((prev) => (prev === heroPreviewImage ? prev : heroPreviewImage));
    }, [heroPreviewImage, postQuery.isError, postQuery.isFetching]);

    const renderToasts = () => (
        <div className="editor-toast-stack" aria-live="polite" aria-atomic="true">
            {toasts.map((toast) => (
                <div key={toast.id} className={`editor-toast editor-toast--${toast.type}`}>
                    <span>{toast.message}</span>
                    <button type="button" onClick={() => dismissToast(toast.id)} aria-label="Dismiss notification">
                        ✕
                    </button>
                </div>
            ))}
        </div>
    );

    if (!numericPostId) {
        return (
            <div className="page-container editor-shell">
                <div className="editor-blank">
                    <h1>Preparing new post…</h1>
                    <p>Creating a fresh draft before opening the studio.</p>
                </div>
                {renderToasts()}
            </div>
        );
    }

    if (postQuery.isLoading) {
        return (
            <div className="page-container editor-shell">
                <div className="editor-blank">
                    <h1>Loading post…</h1>
                    <p>Fetching blog post content and metadata.</p>
                </div>
                {renderToasts()}
            </div>
        );
    }

    if (postQuery.isError || !postQuery.data) {
        return (
            <div className="page-container editor-shell">
                <div className="editor-blank">
                    <h1>Post unavailable</h1>
                    <p>We could not load the selected post. Try refreshing the page.</p>
                    <button type="button" className="editor-button" onClick={() => postQuery.refetch()}>
                        Retry
                    </button>
                </div>
                {renderToasts()}
            </div>
        );
    }

    const post = postQuery.data;
    const livePreviewMarkup =
        (computedPreviewHtml && computedPreviewHtml.trim().length > 0
            ? computedPreviewHtml
            : '<p class="preview-hint">Start writing to see a structured preview.</p>');
    const sanitizedPreviewMarkup = previewMutation.isPending
        ? '<p class="preview-hint">Generating sanitized preview…</p>'
        : previewHtml && previewHtml.trim().length > 0
            ? previewHtml
            : '<p class="preview-hint">The sanitized preview appears here once generated.</p>';
    const previewMarkup = previewMode === 'sanitized' ? sanitizedPreviewMarkup : livePreviewMarkup;
    const cardPreviewTitle = formState.title.trim() ? formState.title.trim() : 'Untitled post';
    const cardPreviewExcerpt = formState.excerpt.trim()
        ? formState.excerpt.trim()
        : 'We are preparing the full story for this article.';
    const cardPreviewImage = cardImagePreview ?? heroPreviewImage;
    const formatCardDate = (value: string | null) => {
        if (!value) {
            return 'DATE TBD';
        }
        try {
            return new Intl.DateTimeFormat('en', {
                month: 'short',
                day: '2-digit',
                year: 'numeric'
            })
                .format(new Date(value))
                .toUpperCase();
        } catch (error) {
            console.error('Failed to format card date', error);
            return value;
        }
    };
    const cardPreviewDate = formatCardDate(post.published_at ?? post.updated_at ?? null);
    const cardPreviewReadingTime =
        formState.readingTime && formState.readingTime > 0
            ? `${formState.readingTime} MIN READ`
            : '';
    const hasHeroImage = Boolean(post.hero?.filename);
    const isCardImageUploading = cardImageUploadMutation.isPending;
    const isCardImageRemoving = cardImageRemoveMutation.isPending;

    const statusPrimaryAction = (() => {
        if (post.status === 'PUBLISHED') {
            return {
                label: 'Unpublish',
                tone: 'ghost' as const,
                onClick: () => unpublishMutation.mutate()
            };
        }
        if (post.status === 'ARCHIVED') {
            return {
                label: 'Restore',
                tone: 'ghost' as const,
                onClick: () => restoreMutation.mutate()
            };
        }
        return {
            label: 'Publish',
            tone: 'accent' as const,
            onClick: () => publishMutation.mutate()
        };
    })();

    const statusSecondaryActions = [
        post.status !== 'ARCHIVED'
            ? {
                key: 'archive',
                label: 'Archive post',
                onClick: () => archiveMutation.mutate()
            }
            : null,
        post.status === 'PUBLISHED'
            ? null
            : {
                key: 'publish',
                label: 'Publish now',
                onClick: () => publishMutation.mutate()
            }
    ].filter(Boolean) as { key: string; label: string; onClick: () => void }[];

    // ─── Variant B layout: 2-pane (content left, metadata sidebar right) ───
    const words = formState.bodyMarkdown.trim().split(/\s+/).filter(Boolean).length;
    const readingMinutes = Math.max(1, Math.round(words / 200));
    const imageCount = formState.contentDocument.blocks?.filter((b: { type: string }) => b.type === 'image').length ?? 0;
    const titleCharCount = formState.pageTitle.length || formState.title.length;
    const lastSavedLabel = lastSavedAt
        ? `Saved ${formatRelative(Date.now() - lastSavedAt)} ago`
        : 'Not yet saved';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* ─── Slim top bar ─── */}
            <div className="hf-sticky-bar" style={{
                padding: '10px 20px',
                background: 'var(--surface-2)',
                borderBottom: '1px solid var(--border)',
                borderTop: 'none',
                top: 0
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <button className="hf-btn ghost" onClick={() => navigate('/blog/posts')}>
                        <ArrowLeft size={14} /> Posts
                    </button>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 8 }}>
                            Edit post
                            <span className={`hf-tag sm ${post.status === 'PUBLISHED' ? 'success' : post.status === 'SCHEDULED' ? 'warning' : post.status === 'ARCHIVED' ? 'outline' : 'accent'}`}>
                                {post.status.toLowerCase()}
                            </span>
                        </span>
                        <span className="hf-help" style={{ marginTop: 0 }}>{lastSavedLabel}</span>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div className="hf-segmented">
                        <button className={previewMode === 'realtime' ? 'on' : ''} onClick={() => setPreviewMode('realtime')}>
                            <Eye size={12} /> Live
                        </button>
                        <button className={previewMode === 'sanitized' ? 'on' : ''} onClick={() => setPreviewMode('sanitized')}>
                            Sanitized
                        </button>
                    </div>
                    <button className="hf-btn" onClick={handleSave} disabled={updateMutation.isPending}>
                        <Save size={14} /> {updateMutation.isPending ? 'Saving…' : 'Save'}
                    </button>
                    {post.status !== 'PUBLISHED' && (
                        <button className="hf-btn primary" onClick={() => publishMutation.mutate()} disabled={isBusy}>
                            <Send size={14} /> Publish
                        </button>
                    )}
                    {post.status === 'PUBLISHED' && (
                        <button className="hf-btn" onClick={() => unpublishMutation.mutate()} disabled={isBusy}>
                            Unpublish
                        </button>
                    )}
                    {post.status === 'ARCHIVED' && (
                        <button className="hf-btn" onClick={() => restoreMutation.mutate()} disabled={isBusy}>
                            <RotateCcw size={14} /> Restore
                        </button>
                    )}
                </div>
            </div>

            {renderToasts()}

            {/* ─── Two-pane body ─── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 0, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
                {/* Content pane */}
                <main style={{ padding: '20px 24px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Title input — large, borderless */}
                    <input
                        type="text"
                        value={formState.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Untitled post"
                        style={{
                            all: 'unset',
                            display: 'block',
                            fontFamily: 'var(--font-sans)',
                            fontSize: 28,
                            fontWeight: 600,
                            letterSpacing: '-0.02em',
                            color: 'var(--fg-1)',
                            width: '100%',
                            lineHeight: 1.2,
                            padding: '2px 0'
                        }}
                    />

                    <div className="hf-divider dashed" />

                    {/* Hero image slot */}
                    <div
                        style={{
                            position: 'relative',
                            width: '100%',
                            height: 220,
                            borderRadius: 'var(--r-2)',
                            background: 'var(--surface-2)',
                            border: '1px solid var(--border)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                        }}
                    >
                        {cardPreviewImage ? (
                            <>
                                <img src={cardPreviewImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                {hasHeroImage && (
                                    <button
                                        type="button"
                                        className="hf-btn icon"
                                        style={{ position: 'absolute', top: 8, right: 8 }}
                                        onClick={() => cardImageRemoveMutation.mutate()}
                                        disabled={isCardImageRemoving}
                                        title="Remove hero image"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </>
                        ) : (
                            <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--fg-3)' }}>
                                <ImageIcon size={28} />
                                <span style={{ fontSize: 13 }}>{isCardImageUploading ? 'Uploading…' : 'Click to add hero image'}</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) { cardImageUploadMutation.mutate(file); e.target.value = ''; }
                                    }}
                                    disabled={isCardImageUploading || isCardImageRemoving}
                                />
                            </label>
                        )}
                    </div>

                    {/* Lexical body */}
                    <div className="editor-content" style={{ flex: 1, minHeight: 320 }}>
                        <LexicalContentEditor
                            key={editorKey}
                            value={formState.contentDocument}
                            onChange={handleContentChange}
                            onRequestImageUpload={numericPostId ? handleRichImageUpload : undefined}
                            isImageUploading={isInlineImageUploading}
                            resolveImageSrc={composeImageUrl}
                        />
                    </div>

                    {/* Sanitized preview (only when toggled) */}
                    {previewMode === 'sanitized' && (
                        <div className="hf-card">
                            <div className="hf-card-header"><h3 className="hf-card-title">Sanitized preview</h3></div>
                            <div className="hf-card-body editor-preview" dangerouslySetInnerHTML={{ __html: previewMarkup }} />
                        </div>
                    )}
                </main>

                {/* Metadata sidebar */}
                <aside style={{
                    background: 'var(--surface-2)',
                    borderLeft: '1px solid var(--border)',
                    padding: '20px 18px',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14
                }}>
                    {/* Status card */}
                    <div className="hf-card">
                        <div className="hf-card-header"><h3 className="hf-card-title">Status</h3></div>
                        <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                                <div className="hf-label-row"><span className="hf-label">State</span></div>
                                <div className="hf-tag accent">{post.status.toLowerCase()}</div>
                            </div>
                            <div>
                                <div className="hf-label-row"><span className="hf-label">Visibility</span></div>
                                <select
                                    className="hf-select"
                                    value={formState.visibility}
                                    onChange={(e) => handleInputChange('visibility', e.target.value as PostVisibility)}
                                >
                                    <option value="PUBLIC">Public</option>
                                    <option value="UNLISTED">Unlisted</option>
                                    <option value="PRIVATE">Private</option>
                                </select>
                            </div>
                            <div>
                                <div className="hf-label-row"><span className="hf-label">Publish at</span></div>
                                <input
                                    type="datetime-local"
                                    className="hf-textarea"
                                    style={{ minHeight: 32, padding: '7px 11px', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}
                                    value={scheduleValue}
                                    onChange={(e) => setScheduleValue(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="hf-btn sm"
                                    style={{ marginTop: 6, width: '100%' }}
                                    onClick={handleSchedule}
                                    disabled={scheduleMutation.isPending}
                                >
                                    <CalendarClock size={12} /> Schedule
                                </button>
                            </div>
                            {(post.published_at || post.scheduled_at) && (
                                <div className="hf-help" style={{ marginTop: 0 }}>
                                    {post.published_at && <>Published: <span className="hf-mono">{new Date(post.published_at).toLocaleString()}</span><br /></>}
                                    {post.scheduled_at && <>Scheduled: <span className="hf-mono">{new Date(post.scheduled_at).toLocaleString()}</span></>}
                                </div>
                            )}
                            {post.status !== 'ARCHIVED' && (
                                <button
                                    type="button"
                                    className="hf-btn ghost sm"
                                    onClick={() => { if (window.confirm('Archive this post?')) archiveMutation.mutate(); }}
                                    disabled={isBusy}
                                >
                                    <Trash2 size={12} /> Archive
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Card image / tags card */}
                    <div className="hf-card">
                        <div className="hf-card-header"><h3 className="hf-card-title">Excerpt & card</h3></div>
                        <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                                <div className="hf-label-row"><span className="hf-label">Excerpt</span></div>
                                <textarea
                                    className="hf-textarea"
                                    rows={3}
                                    value={formState.excerpt}
                                    onChange={(e) => handleInputChange('excerpt', e.target.value)}
                                    placeholder="Short intro shown on listings"
                                />
                            </div>
                        </div>
                    </div>

                    {/* SEO card */}
                    <div className="hf-card">
                        <div className="hf-card-header"><h3 className="hf-card-title">SEO</h3></div>
                        <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                                <div className="hf-label-row"><span className="hf-label">Slug</span></div>
                                <div className="hf-input">
                                    <input
                                        type="text"
                                        value={formState.slug}
                                        onChange={(e) => handleInputChange('slug', e.target.value)}
                                        placeholder="my-post"
                                    />
                                </div>
                                {slugCheckFeedback && (
                                    <div className="hf-help" style={{
                                        marginTop: 4,
                                        color: slugCheckFeedback.includes('taken') ? 'var(--danger)'
                                            : slugCheckFeedback.includes('available') ? 'var(--success)' : 'var(--fg-3)'
                                    }}>
                                        {slugCheckFeedback}
                                    </div>
                                )}
                            </div>
                            <div>
                                <div className="hf-label-row">
                                    <span className="hf-label">Meta title</span>
                                    <span className="hf-help" style={{ marginTop: 0 }}>
                                        <span className="hf-mono">{titleCharCount}</span>/60
                                    </span>
                                </div>
                                <div className="hf-input">
                                    <input
                                        type="text"
                                        value={formState.pageTitle}
                                        onChange={(e) => handleInputChange('pageTitle', e.target.value)}
                                        placeholder="(falls back to title)"
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="hf-label-row">
                                    <span className="hf-label">Meta description</span>
                                    <span className="hf-help" style={{ marginTop: 0 }}>
                                        <span className="hf-mono">{formState.metaDescription.length}</span>/160
                                    </span>
                                </div>
                                <textarea
                                    className="hf-textarea"
                                    rows={3}
                                    maxLength={160}
                                    value={formState.metaDescription}
                                    onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                                    placeholder="Search & social summary"
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--fg-2)' }}>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <input
                                        type="checkbox"
                                        checked={formState.noindex}
                                        onChange={(e) => handleInputChange('noindex', e.target.checked)}
                                    /> noindex
                                </label>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <input
                                        type="checkbox"
                                        checked={formState.nofollow}
                                        onChange={(e) => handleInputChange('nofollow', e.target.checked)}
                                    /> nofollow
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Stats card */}
                    <div className="hf-card">
                        <div className="hf-card-header">
                            <h3 className="hf-card-title">Stats</h3>
                            <FileText size={13} style={{ color: 'var(--fg-3)' }} />
                        </div>
                        <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <StatRow label="Words" value={<span className="hf-mono">{words}</span>} />
                            <StatRow label="Reading time" value={
                                <span className="hf-mono">
                                    {formState.readingTime || readingMinutes} min
                                </span>
                            } />
                            <StatRow label="Images" value={<span className="hf-mono">{imageCount}</span>} />
                            <div className="hf-divider dashed" />
                            <div>
                                <div className="hf-label-row"><span className="hf-label">Override reading time</span></div>
                                <div className="hf-input">
                                    <input
                                        type="number"
                                        min={0}
                                        value={formState.readingTime}
                                        onChange={(e) => handleInputChange('readingTime', Number(e.target.value))}
                                        placeholder="auto"
                                    />
                                    <span className="hf-help" style={{ marginTop: 0 }}>min</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

const StatRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
        <span style={{ color: 'var(--fg-3)' }}>{label}</span>
        <span style={{ color: 'var(--fg-1)' }}>{value}</span>
    </div>
);

function formatRelative(ms: number): string {
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    return `${h}h`;
}

export default BlogPostEditorPage;
